"use client";

import React, { useContext, useCallback, useMemo, useRef } from "react";
import { useSyncExternalStore } from "react";
import { SelectionContext, SelectionState, SelectionUpdateInput } from "./SelectedItemsContext";
import { selectionStore } from "./SelectionStore";

/**
 * Selection provider that uses a store-based approach for fine-grained subscriptions.
 * The store handles selection state and notifies only affected components.
 */
export default function SelectedItemsProvider({ children }: { children: React.ReactNode }) {
  // Cache for snapshot to maintain reference stability
  const snapshotCache = useRef<{ version: number; snapshot: SelectionState } | null>(null);

  // Subscribe to the store for backward compatibility with components that use the full state
  const subscribe = useCallback(
    (callback: () => void) => selectionStore.subscribeGlobal(callback),
    []
  );

  const getSnapshot = useCallback((): SelectionState => {
    const currentVersion = selectionStore.getVersion();

    // Return cached snapshot if version hasn't changed
    if (snapshotCache.current && snapshotCache.current.version === currentVersion) {
      return snapshotCache.current.snapshot;
    }

    // Create new snapshot and cache it
    const snapshot: SelectionState = {
      kitGallery: selectionStore.getSelection("kitCanvas"),
      assetLibrary: selectionStore.getSelection("assetLibrary"),
      tagLibrary: selectionStore.getSelection("tagLibrary"),
      portal: selectionStore.getSelection("portal"),
      dashboard: selectionStore.getSelection("dashboard"),
    };

    snapshotCache.current = { version: currentVersion, snapshot };
    return snapshot;
  }, []);

  const selectionState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const selectItems = useCallback(({ type, ids }: SelectionUpdateInput) => {
    selectionStore.selectItems({ type, ids });
  }, []);

  const deselectItems = useCallback(({ type, ids }: SelectionUpdateInput) => {
    selectionStore.deselectItems({ type, ids });
  }, []);

  const clearSelection = useCallback(({ type }: SelectionUpdateInput) => {
    selectionStore.clearSelection({ type });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const context = useMemo(
    () => ({
      selectionState,
      selectItems,
      deselectItems,
      clearSelection,
    }),
    [selectionState, selectItems, deselectItems, clearSelection]
  );

  return <SelectionContext.Provider value={context}>{children}</SelectionContext.Provider>;
}

export const useSelectedItemsContext = () => useContext(SelectionContext);

// Re-export hooks for direct store access (preferred for performance)
export { useIsItemSelected, useSelection, useSelectionActions } from "./useSelectionHooks";
